import type { PropsWithChildren, FunctionComponent } from 'react'
import { useEffect, useMemo, useRef } from 'react'
import { isEqual } from 'lodash-es'
import { Loader } from '@retailer-platform/shared-components/src/common/loader/Loader'
import { ErrorPage } from '../../../gin-and-tonic/containers/error-page/ErrorPage'
import { usePartnerId } from '../../routing/params.hooks'
import { useMeasureTiming } from '../../events/hooks'
import { StaleDataNotification } from '../warehouse/StaleDataNotification'
import { hasOnlyRestrictedBanners } from '../../../legacy/components/AccessControl/accessControl.utils'
import { PartnerContext, type PartnerContextValue } from './PartnerContext'
import { useFetchPartner } from './queries/partner'

interface Props {
  id: string
}

const initialState: PartnerContextValue = {
  loaded: false,
  loading: false,
  error: false,
  partnerId: undefined,
  partnerName: undefined,
  partnerType: undefined,
  partner: undefined,
  validId: undefined,
  locationFeatures: undefined,
  warehouses: undefined,
  featureToggles: undefined,
  featureEntitlements: undefined,
  hasIaNavV2: undefined,
  isRestricted: undefined,
}

const PartnerProvider: FunctionComponent<PropsWithChildren<Props>> = ({ children, id }) => {
  const triggerMeasureEnd = useMeasureTiming(
    {
      id: 'partner_provider.loaded',
      description:
        'Time taken to load application initialization data (retailer locations, retailer locales, etc) from PartnerProvider mount to full load. Note that there may be additional variance due to retailer loading time (not cached due to roulette flags living there)',
    },
    []
  )

  const {
    partner,
    partnerName,
    partnerType,
    warehouses,
    locationFeatures,
    featureToggles,
    featureEntitlements,
    loading,
    error,
    errorWhileRevalidating,
    refetch,
  } = useFetchPartner(id)

  const partnerId = usePartnerId()
  const isRestricted = hasOnlyRestrictedBanners(warehouses)
  // @ts-expect-error TS7006 — strictNullChecks. TODO: fix strict null check
  const hasIaNavV2 = !!featureToggles?.find(feature => feature.name === 'rt_ia_nav_v2')

  // Derive context value synchronously during render instead of via effects.
  // This eliminates the re-render cascade that was caused by 3 separate
  // useEffect hooks each calling setContextValue.
  // @ts-expect-error TS2345 — strictNullChecks. TODO: fix strict null check
  const contextValue = useMemo<PartnerContextValue>(() => {
    if (error) {
      return {
        loaded: false,
        loading: false,
        error: true,
        partnerId: undefined,
        partnerName: undefined,
        partnerType: undefined,
        partner: undefined,
        validId: undefined,
        locationFeatures: undefined,
        warehouses: undefined,
        featureToggles: undefined,
        featureEntitlements: undefined,
        hasIaNavV2: undefined,
        isRestricted: undefined,
      }
    }
    if (partner) {
      return {
        loaded: true,
        loading: false,
        error: false,
        partnerName,
        partnerType,
        partnerId,
        partner,
        validId: true,
        warehouses,
        locationFeatures,
        featureToggles,
        featureEntitlements,
        hasIaNavV2,
        isRestricted,
      }
    }
    if (loading) {
      return {
        loaded: false,
        loading: true,
        error: false,
        partnerId: undefined,
        partnerName: undefined,
        partnerType: undefined,
        partner: undefined,
        validId: undefined,
        locationFeatures: undefined,
        warehouses: undefined,
        featureToggles: undefined,
        featureEntitlements: undefined,
        hasIaNavV2: undefined,
        isRestricted: undefined,
      }
    }
    return initialState
  }, [
    error,
    partner,
    loading,
    partnerId,
    partnerName,
    partnerType,
    warehouses,
    locationFeatures,
    featureToggles,
    featureEntitlements,
    hasIaNavV2,
    isRestricted,
  ])

  // Stabilize the context reference: only produce a new object when the
  // value is deeply different. This prevents unnecessary consumer re-renders
  // when useFetchPartner returns new object references for identical data.
  const stableContextRef = useRef(contextValue)
  if (!isEqual(stableContextRef.current, contextValue)) {
    stableContextRef.current = contextValue
  }

  // Track the last partner ID for which we successfully loaded data.
  // This replaces the old effect-lag-based switching detection.
  const loadedPartnerIdRef = useRef<string | undefined>()
  if (stableContextRef.current.loaded) {
    loadedPartnerIdRef.current = partnerId
  }
  const switching =
    !stableContextRef.current.loaded &&
    loadedPartnerIdRef.current !== undefined &&
    partnerId !== loadedPartnerIdRef.current

  useEffect(() => {
    if (!loading && !error)
      triggerMeasureEnd({
        retailerId: id,
        cacheEnabled: !!featureToggles
          // @ts-expect-error TS7006 — strictNullChecks. TODO: fix strict null check
          .map?.(v => v.name)
          .includes?.('rt_retailer_stale_while_revalidate_enabled'),
      })
  }, [error, featureToggles, id, loading, triggerMeasureEnd])

  if (error) return <ErrorPage action={() => refetch()} />

  return (
    <PartnerContext.Provider value={stableContextRef.current}>
      {errorWhileRevalidating && <StaleDataNotification refetch={refetch} />}

      {stableContextRef.current.loaded && !switching ? children : <Loader />}
    </PartnerContext.Provider>
  )
}

export default PartnerProvider
